/**
 * CanvasControls
 *
 * Replacement for ReactFlow's built-in `<Controls>` button cluster that
 * wraps every button in a Fluent UI `<Tooltip>`. We have to render the
 * underlying buttons ourselves because xyflow's `ControlButton`
 * component is a plain function — it does NOT use `forwardRef` — so
 * Fluent Tooltip cannot anchor onto it and the tooltip never appears
 * (this was the root cause of the missing canvas hover affordances on
 * Mac, where the browser's native `title` attribute is also unreliable).
 *
 * Renders into a `<Controls>` panel with all built-ins disabled
 * (`showZoom={false} showFitView={false} showInteractive={false}`) so
 * the parent still gets the standard ReactFlow positioning, sizing,
 * and chrome.
 */

import { useCallback } from 'react';
import {
  Tooltip,
  makeStyles,
  mergeClasses,
  Spinner,
} from '@coreai/fluentui-react';
import {
  AddRegular,
  SubtractRegular,
  ScaleFitRegular,
  LockClosedRegular,
  LockOpenRegular,
  GridDotsRegular,
  OrganizationHorizontalRegular,
  OrganizationRegular,
} from '@fluentui/react-icons';
import { useReactFlow, useStore } from '@xyflow/react';
import { shallow } from 'zustand/shallow';
import { getShortcutLabel } from '@/utils/input';

const FORMAT_WORKFLOW_HINT = getShortcutLabel('formatWorkflow');

export interface CanvasControlsProps {
  /** Layout orientation — controls which icon shows for the toggle */
  isHorizontal: boolean;
  /** Whether an auto-layout pass is currently in progress */
  isLayouting?: boolean;
  /** Callback when the user clicks "Auto-arrange" */
  onAutoArrange?: () => void;
  /** Callback when the user clicks the layout-direction toggle */
  onToggleOrientation: () => void;
  /**
   * Whether to render the auto-arrange button. Off in monitoring mode
   * because the canvas layout is computed from a fixed run snapshot.
   */
  showAutoArrange?: boolean;
  /** Whether to render the orientation toggle button. */
  showOrientationToggle?: boolean;
  /**
   * Whether to render the interactive (lock) toggle. Off in monitoring
   * mode because the canvas is read-only.
   */
  showInteractive?: boolean;
  /**
   * Controlled lock state. The lock must be lifted because xyflow re-syncs
   * its `nodesDraggable` / `nodesConnectable` / `elementsSelectable` props
   * into its internal store on every render — mutating the store directly
   * from here would be immediately overwritten by the parent's props.
   */
  isLocked?: boolean;
  /** Callback fired when the lock button is clicked. */
  onToggleLock?: () => void;
}

const useStyles = makeStyles({
  // Override ReactFlow's default `react-flow__controls-button` SVG
  // sizing so Fluent icons (which have intrinsic 20px viewBoxes) render
  // at the same visual weight as xyflow's bundled icons.
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// Selector reads zoom-only state from xyflow. Lock state is owned by the
// parent (passed via props) so toggling it survives ReactFlow's prop sync.
const zoomSelector = (s: {
  minZoom: number;
  maxZoom: number;
  transform: [number, number, number];
}) => ({
  minZoomReached: s.transform[2] <= s.minZoom,
  maxZoomReached: s.transform[2] >= s.maxZoom,
});

export const CanvasControls = ({
  isHorizontal,
  isLayouting = false,
  onAutoArrange,
  onToggleOrientation,
  showAutoArrange = true,
  showOrientationToggle = true,
  showInteractive = true,
  isLocked = false,
  onToggleLock,
}: CanvasControlsProps) => {
  const styles = useStyles();
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const { minZoomReached, maxZoomReached } = useStore(zoomSelector, shallow);

  const handleZoomIn = useCallback(() => {
    zoomIn({ duration: 200 });
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut({ duration: 200 });
  }, [zoomOut]);

  const handleFitView = useCallback(() => {
    fitView({ duration: 300 });
  }, [fitView]);

  const buttonClass = mergeClasses(
    'react-flow__controls-button',
    styles.iconButton,
  );

  return (
    <>
      <Tooltip content="Zoom in" relationship="label">
        <button
          type="button"
          className={mergeClasses(buttonClass, 'react-flow__controls-zoomin')}
          onClick={handleZoomIn}
          disabled={maxZoomReached}
          aria-label="Zoom in"
        >
          <AddRegular />
        </button>
      </Tooltip>

      <Tooltip content="Zoom out" relationship="label">
        <button
          type="button"
          className={mergeClasses(buttonClass, 'react-flow__controls-zoomout')}
          onClick={handleZoomOut}
          disabled={minZoomReached}
          aria-label="Zoom out"
        >
          <SubtractRegular />
        </button>
      </Tooltip>

      <Tooltip content="Fit view" relationship="label">
        <button
          type="button"
          className={mergeClasses(buttonClass, 'react-flow__controls-fitview')}
          onClick={handleFitView}
          aria-label="Fit view"
        >
          <ScaleFitRegular />
        </button>
      </Tooltip>

      {showInteractive && onToggleLock && (
        <Tooltip
          content={isLocked ? 'Unlock canvas' : 'Lock canvas'}
          relationship="label"
        >
          <button
            type="button"
            className={mergeClasses(
              buttonClass,
              'react-flow__controls-interactive',
            )}
            onClick={onToggleLock}
            aria-label={isLocked ? 'Unlock canvas' : 'Lock canvas'}
            aria-pressed={isLocked}
          >
            {isLocked ? <LockClosedRegular /> : <LockOpenRegular />}
          </button>
        </Tooltip>
      )}

      {showAutoArrange && onAutoArrange && (
        <Tooltip
          content={`Auto-arrange nodes (${FORMAT_WORKFLOW_HINT})`}
          relationship="label"
        >
          <button
            type="button"
            className={buttonClass}
            onClick={onAutoArrange}
            disabled={isLayouting}
            aria-label="Auto-arrange nodes"
            aria-keyshortcuts="Shift+Alt+F Shift+Meta+F"
          >
            {isLayouting ? <Spinner size="tiny" /> : <GridDotsRegular />}
          </button>
        </Tooltip>
      )}

      {showOrientationToggle && (
        <Tooltip
          content={
            isHorizontal
              ? 'Switch to vertical layout'
              : 'Switch to horizontal layout'
          }
          relationship="label"
        >
          <button
            type="button"
            className={buttonClass}
            onClick={onToggleOrientation}
            aria-label={
              isHorizontal
                ? 'Switch to vertical layout'
                : 'Switch to horizontal layout'
            }
          >
            {isHorizontal ? (
              <OrganizationHorizontalRegular />
            ) : (
              <OrganizationRegular />
            )}
          </button>
        </Tooltip>
      )}
    </>
  );
};
